import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, Sparkles, FileSearch, Camera, ChevronDown } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CameraBackdrop from "@/components/CameraBackdrop";
import { ImageTicker, ImageTickerDark, TextTicker } from "@/components/Ticker";
import { PHOTO_MATCH_GALLERY, AUTHENTICATED_GALLERY, SPORTS_MUSIC_ENT_GALLERY } from "@/data/galleryImages";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

const HERO_IMG = "/images/rgu-hero-banner.jpg";
const BG_STRIP = PHOTO_MATCH_GALLERY.slice(0, 6);

export default function Landing() {
  const navigate = useNavigate();

  return (
    <div className="App bg-white" data-testid="landing-page">
      <Header />

      {/* HERO */}
      <section className="relative rgu-hero-bg">
        {/* Background camera & lens silhouettes */}
        <CameraBackdrop />

        {/* film strip behind hero */}
        <div className="rgu-photo-strip" aria-hidden="true">
          {BG_STRIP.map((src, i) => (
            <div key={i} style={{ backgroundImage: `url(${src})` }} />
          ))}
        </div>

        <div className="rgu-container relative z-10 pt-4 md:pt-6 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <div className="rgu-eyebrow text-[#0A192F] mb-5 flex items-center gap-3">
              <span className="rgu-pulse-dot" /> Photo Match &amp; Traditional Authentication Since 2016
            </div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-[#0A192F] leading-[0.92]">
              Increase the Value<br/>
              of Your <span className="italic" style={{ fontVariationSettings: '"opsz" 96, "wdth" 90' }}>Memorabilia.</span>
            </h1>
            <p className="mt-8 text-lg text-slate-700 max-w-xl leading-relaxed">
              The industry&apos;s most comprehensive photo matching service. Sports, music, film, and entertainment
              memorabilia — authenticated against thousands of archival images, powered by first-in-industry
              AI research tools.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="rgu-btn-primary text-xs uppercase tracking-widest" data-testid="hero-authenticate-btn">
                    Authenticate Now <ChevronDown className="w-4 h-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="rounded-none border border-[#0A192F] w-80 p-0">
                  <DropdownMenuItem
                    className="rounded-none px-5 py-4 cursor-pointer focus:bg-slate-50 border-b border-slate-200"
                    onClick={() => navigate("/submit/photo_match")}
                    data-testid="hero-dropdown-photo-match"
                  >
                    <div>
                      <div className="font-display text-sm text-[#0A192F]">Photo Match Authentication</div>
                      <div className="text-xs text-slate-500 mt-1">Conclusive validation against image archives.</div>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="rounded-none px-5 py-4 cursor-pointer focus:bg-slate-50"
                    onClick={() => navigate("/submit/traditional")}
                    data-testid="hero-dropdown-traditional"
                  >
                    <div>
                      <div className="font-display text-sm text-[#0A192F]">Traditional Authentication</div>
                      <div className="text-xs text-slate-500 mt-1">Mail-in evaluation by senior authenticators.</div>
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <a
                href="https://infiniteauctions.com/consign"
                target="_blank"
                rel="noreferrer"
                className="rgu-btn-secondary text-xs uppercase tracking-widest"
                data-testid="hero-consign-link"
              >
                Consign to Auction <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>

            <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl">
              {[
                { stat: "$35M+", label: "Authenticated" },
                { stat: "2,500+", label: "LOAs Issued" },
                { stat: "30+ YRS", label: "Experience" },
                { stat: "EST. 2016", label: "Real Game Used" },
              ].map((s) => (
                <div key={s.label} className="border-l-2 border-[#0A192F] pl-4 hover:border-amber-500 transition-colors">
                  <div className="font-mono-rgu text-xl md:text-2xl text-[#0A192F]">{s.stat}</div>
                  <div className="rgu-eyebrow text-slate-500 mt-1 text-[10px]">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 order-1 lg:order-2">
            <div className="relative border border-slate-200 shadow-[12px_12px_0_0_rgba(10,25,47,0.08)]">
              <img
                src={HERO_IMG}
                alt="Real Game Used Photo Match Authentication"
                className="w-full h-auto object-cover block"
                width={1600}
                height={1200}
                fetchpriority="high"
                decoding="async"
                onError={(e) => {
                  // Fallback to direct asset URL if local copy ever fails
                  e.currentTarget.src =
                    "https://customer-assets.emergentagent.com/job_game-worn-auth/artifacts/km2xk03w_RGU%20Site%20Banner.png";
                }}
              />
              <div className="bg-[#0A192F] text-white px-5 py-4 border-t border-slate-200 flex items-center justify-between gap-3">
                <div>
                  <div className="rgu-eyebrow text-amber-300 text-[10px]">Online · No Mail-in Required</div>
                  <div className="font-display text-sm md:text-base leading-snug mt-1">
                    Upload Photos, Docs, Details &amp; Pay Online. Submissions are Fast and Easy!
                  </div>
                </div>
                <ShieldCheck className="w-6 h-6 text-amber-300 flex-shrink-0" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TEXT TICKER */}
      <TextTicker text="1,500+ RGU PHOTO MATCHES · 30+ YEARS EXPERIENCE" testId="text-ticker-matches" />

      {/* IMAGE TICKER 1 — RGU Digital Match Gallery */}
      <section className="bg-white">
        <div className="rgu-container pt-20 pb-8">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="rgu-eyebrow text-slate-500 mb-3"><span className="rgu-accent-bar" />RGU Digital Match Gallery</div>
              <h2 className="font-display text-3xl md:text-4xl text-[#0A192F]">1,500+ RGU Photo Matches.</h2>
            </div>
            <div className="hidden md:block text-sm text-slate-500 max-w-xs text-right">
              A curated selection from our archive of conclusive photo match authentications.
            </div>
          </div>
        </div>
        <ImageTicker images={PHOTO_MATCH_GALLERY} testId="image-ticker-photomatches" />
      </section>

      {/* SERVICES */}
      <section id="services" className="bg-white">
        <div className="rgu-container py-24">
          <div className="rgu-eyebrow text-slate-500 mb-3"><span className="rgu-accent-bar" />Services</div>
          <h2 className="font-display text-3xl md:text-4xl text-[#0A192F] max-w-2xl">
            Two paths to authentication. One uncompromising standard.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
            {/* Type 1 */}
            <div className="border border-slate-200 p-8 hover:border-[#0A192F] transition-colors" data-testid="service-photo-match">
              <Camera className="w-8 h-8 text-[#0A192F]" />
              <div className="rgu-eyebrow text-slate-500 mt-6">Type 01 · Photo Match Evaluation</div>
              <h3 className="font-display text-2xl text-[#0A192F] mt-2">Photo Match Authentication</h3>
              <p className="text-slate-600 mt-4 leading-relaxed">
                The industry&apos;s most comprehensive photo matching service. Access to thousands of archival images and
                videos. A conclusive match may increase the value of memorabilia by up to 1,000% or more.
              </p>
              <button
                onClick={() => navigate("/submit/photo_match")}
                className="mt-6 rgu-btn-primary text-xs uppercase tracking-widest"
                data-testid="service-photo-match-btn"
              >
                Submit for Photo Match <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>

            {/* Type 2 */}
            <div className="border border-slate-200 p-8 hover:border-[#0A192F] transition-colors" data-testid="service-traditional">
              <FileSearch className="w-8 h-8 text-[#0A192F]" />
              <div className="rgu-eyebrow text-slate-500 mt-6">Type 02 · Item Evaluation</div>
              <h3 className="font-display text-2xl text-[#0A192F] mt-2">Traditional Authentication</h3>
              <p className="text-slate-600 mt-4 leading-relaxed">
                Hands-on, mail-in authentication. Senior authenticators examine memorabilia for provenance and intricate
                details, compared against proven exemplars for the most accurate opinion possible.
              </p>
              <button
                onClick={() => navigate("/submit/traditional")}
                className="mt-6 rgu-btn-secondary text-xs uppercase tracking-widest"
                data-testid="service-traditional-btn"
              >
                Submit for Traditional <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* AI feature — Futuristic AI panel */}
          <div
            className="mt-16 relative overflow-hidden border border-[#0A192F] bg-[#06101F] text-white"
            data-testid="ai-banner"
          >
            {/* Neural mesh background */}
            <div
              className="absolute inset-0 opacity-40"
              style={{
                background:
                  "radial-gradient(900px 400px at 12% 10%, rgba(37,99,235,0.45), transparent 60%), radial-gradient(700px 500px at 90% 110%, rgba(245,158,11,0.30), transparent 60%), radial-gradient(500px 500px at 60% 50%, rgba(168,85,247,0.18), transparent 60%)",
              }}
              aria-hidden="true"
            />
            {/* Animated scanning line */}
            <div className="rgu-ai-scan" aria-hidden="true" />
            {/* Grid */}
            <div
              className="absolute inset-0 opacity-[0.10] pointer-events-none"
              style={{
                backgroundImage:
                  "linear-gradient(to right, rgba(96,165,250,0.6) 1px, transparent 1px), linear-gradient(to bottom, rgba(96,165,250,0.6) 1px, transparent 1px)",
                backgroundSize: "44px 44px",
                maskImage: "radial-gradient(ellipse at center, #000 30%, transparent 80%)",
              }}
              aria-hidden="true"
            />
            {/* Floating data nodes */}
            <span className="rgu-ai-node" style={{ top: "20%", left: "8%", animationDelay: "0s" }} />
            <span className="rgu-ai-node" style={{ top: "70%", left: "22%", animationDelay: ".8s" }} />
            <span className="rgu-ai-node" style={{ top: "30%", right: "12%", animationDelay: "1.4s" }} />
            <span className="rgu-ai-node" style={{ top: "60%", right: "28%", animationDelay: "2.1s" }} />

            <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8 items-center p-10 md:p-14">
              <div className="md:col-span-1">
                <div className="relative w-14 h-14 flex items-center justify-center">
                  <div className="absolute inset-0 rounded-full border border-amber-300/60 animate-ping" />
                  <div className="absolute inset-2 rounded-full border border-blue-300/40" />
                  <Sparkles className="w-7 h-7 text-amber-300 relative" />
                </div>
              </div>
              <div className="md:col-span-7">
                <div className="rgu-eyebrow text-amber-300 mb-2 flex items-center gap-3">
                  <span className="rgu-pulse-dot" style={{ background: "#FCD34D" }} /> First in Industry · Live
                </div>
                <h3 className="font-display text-2xl md:text-4xl leading-tight">
                  AI-Enhanced Research <span className="italic text-amber-300">Tools.</span>
                </h3>
                <p className="text-blue-100/80 mt-4 leading-relaxed max-w-2xl">
                  Groundbreaking AI tooling that expands photo-matching research across the entire history of the
                  internet. The backbone of authentication is unparalleled accuracy.
                </p>
              </div>
              <div className="md:col-span-4 md:text-right">
                <div className="font-mono-rgu text-5xl md:text-6xl text-amber-300 leading-none">∞</div>
                <div className="rgu-eyebrow text-blue-200/70 mt-2">Archive Depth</div>
                <div className="mt-3 font-mono-rgu text-xs text-blue-300/80">99.97% Confidence Threshold</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* IMAGE TICKER 2 — Memorabilia Gallery / $35M+ Authenticated */}
      <section className="bg-white">
        <div className="rgu-container pt-10 pb-8">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="rgu-eyebrow text-slate-500 mb-3"><span className="rgu-accent-bar" />Memorabilia Gallery</div>
              <h2 className="font-display text-3xl md:text-4xl text-[#0A192F]">
                Over $35,000,000 Authenticated &amp; 30+ Years of Experience.
              </h2>
            </div>
            <div className="hidden md:block text-sm text-slate-500 max-w-xs text-right">
              Three decades of authentication across sports, music, film and entertainment.
            </div>
          </div>
        </div>
        <ImageTicker images={AUTHENTICATED_GALLERY} testId="image-ticker-authenticated" speed={35} lightTiles />
      </section>

      {/* TEXT TICKER 2 */}
      <TextTicker text="OVER $35,000,000 AUTHENTICATED · 30+ YEARS OF EXPERIENCE · 2,500+ LOAs ISSUED" testId="text-ticker-authenticated" />

      {/* PRICING */}
      <section id="pricing" className="bg-slate-50 border-y border-slate-200">
        <div className="rgu-container py-24">
          <div className="rgu-eyebrow text-slate-500 mb-3"><span className="rgu-accent-bar" />Pricing</div>
          <h2 className="font-display text-3xl md:text-4xl text-[#0A192F] max-w-2xl">
            Transparent, value-based pricing.
          </h2>
          <p className="text-slate-600 mt-4 max-w-2xl">
            Authentication takes up to 20 business days. Expedited service available — 5 business day express.
            Multi-item discount tiers: 2+ items 5% off · 5+ items 7.5% off · 10+ items 10% off.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-12">
            {/* Photo Match table */}
            <div className="bg-white border border-slate-200 p-6" data-testid="pricing-photo-match">
              <div className="rgu-eyebrow text-slate-500">Photo Match Authentication</div>
              <h3 className="font-display text-xl text-[#0A192F] mt-1">Per-Item Price by Value</h3>
              <table className="w-full mt-4 font-mono-rgu text-sm">
                <thead>
                  <tr className="text-left text-slate-500 uppercase text-xs border-b border-slate-200">
                    <th className="py-2">Item Value</th>
                    <th className="py-2">Date Known</th>
                    <th className="py-2">Date Unknown</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["$0 – $5K", "$265", "$300"],
                    ["$5K – $10K", "$300", "$400"],
                    ["$10K – $25K", "$425", "$500"],
                    ["$25K – $50K", "$625", "$750"],
                    ["$50K – $100K", "$750", "$1,000"],
                    ["$100K & Up", "$1,200", "$1,500"],
                  ].map((row) => (
                    <tr key={row[0]} className="border-b border-slate-100">
                      <td className="py-2.5">{row[0]}</td>
                      <td>{row[1]}</td>
                      <td>{row[2]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-4 text-xs text-slate-500">Expedited (5 biz day): +$250 per item.</div>
            </div>

            {/* Traditional table */}
            <div className="bg-white border border-slate-200 p-6" data-testid="pricing-traditional">
              <div className="rgu-eyebrow text-slate-500">Traditional Authentication</div>
              <h3 className="font-display text-xl text-[#0A192F] mt-1">Per-Item Price by Value</h3>
              <table className="w-full mt-4 font-mono-rgu text-sm">
                <thead>
                  <tr className="text-left text-slate-500 uppercase text-xs border-b border-slate-200">
                    <th className="py-2">Item Value</th>
                    <th className="py-2">Price</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["$0 – $5K", "$265"],
                    ["$5K – $10K", "$325"],
                    ["$10K – $25K", "$425"],
                    ["$25K – $50K", "$525"],
                    ["$50K – $100K", "$650"],
                    ["$100K & Up", "$750"],
                  ].map((row) => (
                    <tr key={row[0]} className="border-b border-slate-100">
                      <td className="py-2.5">{row[0]}</td>
                      <td>{row[1]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-4 text-xs text-slate-500">Expedited (5 biz day): +50% per item.</div>
            </div>
          </div>

          <div className="mt-8 text-sm text-slate-600 font-mono-rgu">
            Shipping — Domestic $14.95 · International $39.95
          </div>
        </div>
      </section>

      {/* SPORTS, MUSIC & ENTERTAINMENT — exact bs-1 gallery from realgameused.com */}
      <section className="bg-[#0A192F] text-white relative overflow-hidden">
        <div className="rgu-container py-20 relative z-10">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="rgu-eyebrow text-amber-300 mb-3"><span className="rgu-accent-bar" />Vast Memorabilia Expertise</div>
              <h2 className="font-display text-3xl md:text-5xl leading-tight">
                Sports, Music &amp; Entertainment Authentication!
              </h2>
            </div>
            <div className="hidden md:block text-sm text-blue-200/70 max-w-xs text-right">
              Photo-match Letters of Authenticity issued across athletes, musicians and entertainers.
            </div>
          </div>
        </div>
        <div className="relative z-10">
          <ImageTickerDark images={SPORTS_MUSIC_ENT_GALLERY} testId="image-ticker-sports-music-ent" speed={30} />
        </div>
        {/* navy gradient overlay aura */}
        <div className="absolute inset-0 opacity-20 pointer-events-none" style={{
          background: "radial-gradient(800px 400px at 20% 0%, rgba(37,99,235,0.4), transparent 60%), radial-gradient(700px 400px at 80% 100%, rgba(245,158,11,0.25), transparent 60%)",
        }} aria-hidden="true" />
      </section>

      <Footer />
    </div>
  );
}
